import { Flex, Box, Text, useDisclosure, Button } from '@chakra-ui/react';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AnimatedMascot } from 'components/Mascot';
import { ControllableModal } from 'components/Modal';
import { routePaths } from 'global/routePaths';
import { useDisconnectUKG } from 'hooks/integrations/ukg/useDisconnectUKG';
import { useIntegrationStore } from 'store/useIntegrationStore';

import EvvConnectForm from '../ConnectForm';

const Configuration: React.FC<unknown> = () => {
  const { t } = useTranslation(['integrations']);
  const navigate = useNavigate();

  const formTypeRef = useRef<'create' | 'update'>('create');

  const integrationListData: any = useIntegrationStore(state => state.integrationList);
  const ukgIntegrationData = integrationListData.filter((tab: any) => tab.key === 'ukg')[0];

  const { isOpen: loginIsOpen, onOpen: loginOnOpen, onClose: loginOnClose } = useDisclosure();

  const { mutate: disconnectUKG, status: disconnectUKGStatus } = useDisconnectUKG();

  const onConfigureMapping = () => {
    navigate(routePaths.integrations.ukg.mapping);
  };

  const onDisable = () => {
    disconnectUKG();
  };

  const handleEnableClick = () => {
    formTypeRef.current = 'create';
    loginOnOpen();
  };

  const handleUpdateConfigurationClick = () => {
    formTypeRef.current = 'update';
    loginOnOpen();
  };

  return (
    <>
      <ControllableModal isOpen={loginIsOpen} onClose={loginOnClose}>
        <EvvConnectForm onClose={loginOnClose} type={formTypeRef.current} />
      </ControllableModal>

      {!ukgIntegrationData?.is_connected ? (
        <Flex flexDir="column">
          <Box
            padding="20px 40px"
            border="1px solid"
            borderColor="gray.400"
            background="white"
            textAlign="center"
            mb={6}
          >
            <AnimatedMascot type="barking" />
            <Text>{t('integrations:clickToEnable')}</Text>
          </Box>
          <Button ml="auto" onClick={handleEnableClick}>
            {t('integrations:enable')}
          </Button>
        </Flex>
      ) : (
        <Flex flexDirection="column">
          <Box
            padding="20px 40px"
            border="1px solid"
            borderColor="gray.400"
            background="white"
            textAlign="center"
            mb={6}
          >
            <Text>{t('integrations:clickToDisable')}</Text>
          </Box>
          <Box textAlign="right">
            <Button variant="primary-ghost" mr={2} onClick={handleUpdateConfigurationClick}>
              {t('integrations:updateConfiguration')}
            </Button>
            <Button
              _hover={{ bg: 'rgba(255, 103, 32, 0.2)' }}
              background="rgba(255, 103, 32, 0.1)"
              colorScheme="rgba(255, 103, 32, 0.3)"
              color="secondary.500"
              mr={2}
              onClick={onConfigureMapping}
            >
              {t('integrations:viewMapping')}
            </Button>

            <Button
              ml="auto"
              fontWeight="bold"
              background="rgba(250, 85, 85, 0.2)"
              color="rgba(250, 85, 85, 1)"
              _hover={{ bg: 'secondary.100' }}
              isLoading={disconnectUKGStatus === 'loading'}
              onClick={onDisable}
            >
              {t('integrations:generalDisconnect')}
            </Button>
          </Box>
        </Flex>
      )}
    </>
  );
};

export default Configuration;
